import { useCallback, useMemo, useState } from "react";
import type { SchemaRegion } from "../../../api/restaurantSchema";
import type { OpenTable, Reservation } from "../../../api/reservations";
import { useReservationsDay } from "../../../hooks/useReservations";
import {
  DEFAULT_DURATION,
  DEFAULT_GUESTS,
  MAX_GUESTS,
  buildCreatePayload,
  busySelectedTablesOf,
  draftFromReservation,
  guestErrorsOf,
  initialSlot,
  isFormSubmittable,
  isSlotBlockingSave,
  isSlotOutsideHours,
  keepsTablesAndTime,
  reclaimableTableIds,
  reservationsAroundDay,
  seatsSummary,
  slotInterval,
  slotForDay,
  slotOfReservation,
  slotWithHour,
  suggestTable,
  type FormSlot,
  type NewReservationDraft,
  type PickTable,
} from "../logic/newReservation";
import { isTableBusyDuring } from "../logic/tableStatus";
import { followBills } from "../logic/followBills";
import { addDays } from "../logic/dayTime";
import { capitalizeGuestName, normalizeGuestName } from "../logic/guestFields";
import { autofilledGuestName, type GuestLookupAnswer } from "../logic/guestHistory";
import type { NewReservationSeed } from "./useReservationsView";
import { useGuestHistoryLookup } from "./useGuestHistoryLookup";

const NO_RESERVATIONS: Reservation[] = [];
const NO_OPEN_TABLES: OpenTable[] = [];

const freshDraft = (seed: NewReservationSeed | null): NewReservationDraft => ({
  slot: initialSlot(Date.now(), seed?.startMs ?? null),
  durationMinutes: DEFAULT_DURATION,
  guests: DEFAULT_GUESTS,
  tableIds: seed?.tableId != null ? [seed.tableId] : [],
  phone: "",
  guestName: "",
  note: "",
});

/** Текстовите полета на госта. */
export type GuestField = "phone" | "guestName" | "note";

/** Полетата с валидация (грешката се вижда след напускане или опит за запис). */
export type ValidatedGuestField = "phone" | "guestName";

const NOT_TOUCHED: Record<ValidatedGuestField, boolean> = { phone: false, guestName: false };

/** Редактираната резервация и слотът ѝ при отварянето (минал час не спира записа, ако не е сменян). */
interface EditTarget {
  reservation: Reservation;
  originalSlot: FormSlot;
}

/**
 * Състоянието на „Нова резервация" / „Редакция на резервация" — общо за
 * панела вдясно и режима „избор на маса" върху платното. Зарежда
 * резервациите и отворените сметки на ИЗБРАНИЯ в формата ден (може да е друг
 * от показания) и смята кои маси са заети в интервала (при редакция — без
 * самата нея). `reset(seed)` / `startEdit(reservation)` се викат при всяко
 * отваряне на панела.
 */
export const useNewReservationForm = ({
  regions,
  nowMs,
  open,
  enabled,
}: {
  regions: SchemaRegion[];
  nowMs: number;
  // Панелът е отворен (иначе без заявки).
  open: boolean;
  // false — без интернет.
  enabled: boolean;
}) => {
  const [draft, setDraft] = useState<NewReservationDraft>(() => freshDraft(null));
  const [editTarget, setEditTarget] = useState<EditTarget | null>(null);
  // Кои полета са напуснати — грешката им се показва (при опит за запис — всички).
  const [touched, setTouched] = useState(NOT_TOUCHED);
  const editingId = editTarget?.reservation.id ?? null;

  // Познатият телефон: при нова резервация името от последната му
  // резервация влиза само в празно поле или върху предишното попълване
  // (`autofilledGuestName`); при редакция — само историята, без попълване.
  const guestLookup = useGuestHistoryLookup({ phone: draft.phone, excludeId: editingId, enabled: open && enabled });
  const [appliedLookup, setAppliedLookup] = useState<GuestLookupAnswer | null>(null);
  const [autofilledName, setAutofilledName] = useState<string | null>(null);
  if (guestLookup !== appliedLookup) {
    // Нов отговор → веднъж, още в този рендер (без миг с празно име).
    setAppliedLookup(guestLookup);
    const knownName = guestLookup?.history?.guestName;
    const nextName =
      guestLookup && editTarget === null
        ? autofilledGuestName({
            currentName: draft.guestName,
            autofilledName,
            knownName: knownName ? normalizeGuestName(knownName) : null,
          })
        : null;
    if (nextName !== null) {
      setDraft((current) => ({ ...current, guestName: nextName }));
      setAutofilledName(nextName || null);
    }
  }

  const dayQuery = useReservationsDay(draft.slot.dateKey, { enabled: open && enabled });
  // Съседните дни — само за заетостта през полунощ (виж `reservationsAroundDay`).
  const prevDayQuery = useReservationsDay(addDays(draft.slot.dateKey, -1), { enabled: open && enabled });
  const nextDayQuery = useReservationsDay(addDays(draft.slot.dateKey, 1), { enabled: open && enabled });
  const dayQueries = [dayQuery, prevDayQuery, nextDayQuery];
  const openTables = dayQuery.data?.openTables ?? NO_OPEN_TABLES;
  const { fromMs, toMs } = slotInterval(draft.slot, draft.durationMinutes);

  // Всички маси от всички региони (изборът може да е от няколко).
  const tables = useMemo<PickTable[]>(
    () =>
      regions.flatMap((region) =>
        region.elements
          .filter((element) => element.kind === "table")
          .map((table) => ({
            id: table.id,
            name: table.name ?? "",
            seats: table.seats ?? 0,
            regionId: region.id,
          })),
      ),
    [regions],
  );
  // Настанените — на масата на сметката си (огледало на BE синхронизацията).
  const dayReservations = dayQuery.data?.reservations ?? NO_RESERVATIONS;
  const prevDayReservations = prevDayQuery.data?.reservations ?? NO_RESERVATIONS;
  const nextDayReservations = nextDayQuery.data?.reservations ?? NO_RESERVATIONS;
  const reservations = useMemo(
    () =>
      followBills(
        // Избраният ден последен — неговото копие печели при двойка.
        reservationsAroundDay([prevDayReservations, nextDayReservations, dayReservations]),
        openTables,
        tables,
      ),
    [prevDayReservations, nextDayReservations, dayReservations, openTables, tables],
  );

  // Резервациите на избрания в формата ден още не са дошли (при смяна на деня
  // placeholder-ът носи само отворените сметки) — заетостта не е ясна.
  const isLoadingDay = dayQueries.some((query) => query.isLoading || query.isPlaceholderData);
  // Денят не се зареди (без данни за него): заетостта е НЕИЗВЕСТНА — иначе
  // всички маси изглеждат свободни и „Предложи маса" избира резервирана.
  // Неуспешен фонов poll при налични данни не спира формата.
  const dayError = dayQueries.some((query) => query.isError && (!query.data || query.isPlaceholderData));
  // При редакция границата на избора е началото на резервацията (ако е
  // по-рано от „сега") — закъсняла 19:00 може да остане 19:00.
  const originalSlot = editTarget?.originalSlot ?? null;
  const slotPast = isSlotBlockingSave(draft.slot, nowMs, originalSlot);
  // Почивен ден или час извън работното време — записът е спрян.
  const slotOutsideHours = isSlotOutsideHours(draft.slot, originalSlot);

  const busyTableIds = useMemo(() => {
    const floor = { reservations, openTables, nowMs, excludeReservationId: editingId };
    return new Set(
      tables.filter((table) => isTableBusyDuring(table, fromMs, toMs, floor)).map((table) => table.id),
    );
  }, [tables, reservations, openTables, nowMs, fromMs, toMs, editingId]);

  const selectedTables = draft.tableIds
    .map((id) => tables.find((table) => table.id === id))
    .filter((table): table is PickTable => table !== undefined);
  const summary = seatsSummary(selectedTables, draft.guests);
  // Редакция без смяна на масите и часа не се спира от заетостта (гостите
  // може вече да седят — поправка на телефона минава; BE прескача същото).
  const keepsOriginal = editTarget !== null && keepsTablesAndTime(draft, editTarget.reservation);
  const reclaimable = reclaimableTableIds(draft, editTarget?.reservation ?? null);
  const busySelectedTables = busySelectedTablesOf(selectedTables, busyTableIds, reclaimable);

  const guestErrors = guestErrorsOf(draft);
  const visibleErrors = {
    phone: touched.phone ? guestErrors.phone : null,
    guestName: touched.guestName ? guestErrors.guestName : null,
  };

  const reset = useCallback((seed: NewReservationSeed | null) => {
    setEditTarget(null);
    setTouched(NOT_TOUCHED);
    setAutofilledName(null);
    setDraft(freshDraft(seed));
  }, []);

  const startEdit = useCallback((reservation: Reservation) => {
    setEditTarget({ reservation, originalSlot: slotOfReservation(reservation) });
    setTouched(NOT_TOUCHED);
    setAutofilledName(null);
    setDraft(draftFromReservation(reservation));
  }, []);

  const update = (patch: Partial<NewReservationDraft>) => setDraft((current) => ({ ...current, ...patch }));

  return {
    draft,
    // Редактираната резервация (null — нова).
    editing: editTarget?.reservation ?? null,
    // Началото ѝ при отварянето (null — нова): долната граница на избора.
    originalSlot,
    fromMs,
    toMs,
    // Резервациите/сметките на деня още се зареждат (заетостта не е ясна):
    // изборът на маса и „Създай" чакат.
    isLoadingDay,
    // Денят не се зареди — изборът, предложението и „Създай" са спрени.
    dayError,
    // Заетостта не е известна (зарежда се или грешка).
    isDayUnknown: isLoadingDay || dayError,
    // „Опитай пак" — и падналия съседен ден (той също спира формата).
    retryDay: () => {
      for (const query of dayQueries) if (query.isError) void query.refetch();
    },
    // Избраният час мина, докато се попълва формата.
    slotPast,
    // Почивен ден или час извън работното време.
    slotOutsideHours,
    tables,
    busyTableIds,
    // Масите на редактираната резервация при непроменен час — пикерът не ги
    // блокира (махната по грешка се връща).
    reclaimableTableIds: reclaimable,
    selectedTables,
    seats: summary.seats,
    hasEnoughSeats: summary.enough,
    // Избрани маси, които са заети в интервала (след смяна на часа/деня или
    // маса, отворена в Маркиране, докато формата е отворена) — спират записа.
    busySelectedTables,
    // Слот, минал докато се попълва формата (19:15, избран в 19:10), не се
    // праща — BE не проверява за минал час.
    canSubmit: isFormSubmittable({
      draft,
      selectedCount: selectedTables.length,
      busySelectedCount: busySelectedTables.length,
      isDayUnknown: isLoadingDay || dayError,
      // Същите маси и час — заетостта не е нужна (BE прескача проверките).
      keepsOriginal,
      slotPast,
      slotOutsideHours,
    }),
    payload: () => buildCreatePayload({ ...draft, tableIds: selectedTables.map((table) => table.id) }),
    reset,
    startEdit,
    setDay: (dateKey: string) => update({ slot: slotForDay(dateKey, draft.slot.minute, Date.now(), originalSlot) }),
    // Часът от смяната: след полунощ е на следващата дата — плочката горе се сменя с нея.
    setHour: (hour: number, dateKey: string) =>
      update({ slot: slotWithHour({ ...draft.slot, dateKey }, hour, Date.now(), originalSlot) }),
    setMinute: (minute: number) =>
      update({ slot: { ...draft.slot, minute: Math.floor(draft.slot.minute / 60) * 60 + minute } }),
    setGuests: (guests: number) => update({ guests: Math.min(MAX_GUESTS, Math.max(1, guests)) }),
    setDuration: (durationMinutes: number) => update({ durationMinutes }),
    // Името — с главна буква в началото на всяка дума още докато се пише.
    setField: (field: GuestField, value: string) =>
      update({ [field]: field === "guestName" ? capitalizeGuestName(value) : value }),
    // Грешките под полетата: само за напуснатите (или след опит за запис).
    guestErrors: visibleErrors,
    // Историята на познатия телефон (null — непознат, невалиден или още се пише).
    guestHistory: guestLookup?.history ?? null,
    // Напускане на полето: грешката му вече се вижда; името се подрежда
    // (краища, двойни интервали) както ще го запише BE.
    touchField: (field: ValidatedGuestField) => {
      setTouched((current) => (current[field] ? current : { ...current, [field]: true }));
      if (field === "guestName") {
        setDraft((current) => {
          const guestName = normalizeGuestName(current.guestName);
          return guestName === current.guestName ? current : { ...current, guestName };
        });
      }
    },
    // Опит за запис с грешки — показва всички.
    revealErrors: () => setTouched({ phone: true, guestName: true }),
    toggleTable: (tableId: number) =>
      setDraft((current) => ({
        ...current,
        tableIds: current.tableIds.includes(tableId)
          ? current.tableIds.filter((id) => id !== tableId)
          : [...current.tableIds, tableId],
      })),
    /** „☆ Предложи маса" → избира я (заменя избора) и я връща; null — няма такава. */
    suggest: (activeRegionId: number | null): PickTable | null => {
      if (isLoadingDay || dayError) return null;
      const table = suggestTable(tables, draft.guests, (candidate) => busyTableIds.has(candidate.id), activeRegionId);
      if (table) update({ tableIds: [table.id] });
      return table;
    },
  };
};

export type NewReservationForm = ReturnType<typeof useNewReservationForm>;
